import { ToastAction } from '@/components/ui/toast';
import { toast } from '@/hooks/use-toast';
import { useMutation } from '@tanstack/react-query';
import { useCallback, useEffect, useRef } from 'react';
import { pdkAxios } from '../lib/pdk-axios';
import { db } from '../models/db';
import type { Notification } from '../models/registry';
import { notificationStore } from '../models/stores';

type Pending = {
  timeoutId: number;
  notification: Notification;
};

type DeleteNotificationVars = {
  notification: Notification;
  controls: ReturnType<typeof toast>;
};

export function useDeleteNotificationWithUndo() {
  const pending = useRef<Map<string, Pending>>(new Map());

  useEffect(() => {
    return () => {
      for (const { timeoutId } of pending.current.values())
        clearTimeout(timeoutId);
      pending.current.clear();
    };
  }, []);

  const removeLocal = (id: string) => {
    notificationStore.remove(id);
    db.notifications.delete(id); // remove from Dexie
  };

  const restoreLocal = (notification: Notification) => {
    notificationStore.upsert(notification);
    db.notifications.put(notification); // restore in Dexie
  };

  const deleteNotificationMutation = useMutation<
    void,
    Error,
    DeleteNotificationVars
  >({
    mutationFn: async ({ notification }) => {
      await pdkAxios.post(`/notifications/${notification.id}/delete`);
    },

    onSuccess: (_data, { controls }) => {
      // close the toast on success
      controls.dismiss();
    },
    onError: (_err, { notification, controls }) => {
      // rollback + error toast
      restoreLocal(notification);
      controls.dismiss();
      toast({
        title: 'Could not delete',
        description: 'Please try again.',
      });
    },
  });

  const deleteNotification = (notification: Notification, undoMs = 4000) => {
    removeLocal(notification.id);

    let controls: ReturnType<typeof toast>;

    const undo = () => {
      const record = pending.current.get(notification.id);

      // When delete has already been finalized
      if (!record) return;
      clearTimeout(record.timeoutId);
      pending.current.delete(notification.id);

      // When notifications are restored
      restoreLocal(notification);
      controls.update({
        id: controls.id,
        title: 'Notification restored',
        description: undefined,
        action: undefined,
      });
      setTimeout(() => controls.dismiss(), 3000);
    };

    controls = toast({
      title: 'Notification deleted',
      action: (
        <ToastAction altText="Undo delete" onClick={undo}>
          Undo
        </ToastAction>
      ),
    });

    const timeoutId = window.setTimeout(async () => {
      // commit the delete to server
      pending.current.delete(notification.id);
      deleteNotificationMutation.mutate({ notification, controls });
    }, undoMs);

    pending.current.set(notification.id, { timeoutId, notification });
  };

  const mutate = useCallback(
    ({ notification }: { notification: Notification }) =>
      deleteNotification(notification),
    [],
  );

  return { mutate };
}
